import type { FC } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { CodeChip } from '../code-chip';
import { LevelBadge } from '../code-chip/level-badge';
import { splitElectoralCode } from '../helper';
import { CloseSVG } from '../svgs';
import { BreadcrumbStrip } from './breadcrumb-strip';
import { CodePeekBody } from './CodePeekBody';
import { useCodePeek } from './use-code-peek';

export const CodePeekPane: FC = () => {
	const peek = useCodePeek();
	const titleId = useId();
	const paneRef = useRef<HTMLDivElement | null>(null);
	const isOpen = !!peek?.current;

	useEffect(() => {
		if (!isOpen || !peek) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				peek.closePeek();
			}
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [isOpen, peek]);

	useEffect(() => {
		if (!isOpen || !paneRef.current) return;
		const previouslyFocused = document.activeElement as HTMLElement | null;
		const focusable = paneRef.current.querySelector<HTMLElement>(
			'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
		);
		focusable?.focus();
		return () => previouslyFocused?.focus?.();
	}, [isOpen]);

	if (!peek?.current) return null;

	const { current, history, closePeek, promoteToPrimary } = peek;
	const parents = splitElectoralCode(current.code);

	return createPortal(
		<>
			<div
				className='code-peek__scrim'
				onClick={closePeek}
				aria-hidden='true'
			/>
			<aside
				ref={paneRef}
				className='code-peek__pane'
				role='dialog'
				aria-modal='true'
				aria-labelledby={titleId}
			>
				<header className='code-peek__header'>
					<div className='code-peek__title-row'>
						<LevelBadge level={current.level} />
						<h3
							id={titleId}
							className='code-peek__title'
						>
							{current.code}
						</h3>
						<button
							type='button'
							className='code-peek__close'
							aria-label='Close preview'
							onClick={closePeek}
						>
							<CloseSVG color='var(--text-secondary)' />
						</button>
					</div>
					<div className='code-peek__parents'>
						{current.level !== 'state' && parents.state && (
							<CodeChip
								code={parents.state}
								level='state'
								compact
							/>
						)}
						{(current.level === 'ward' || current.level === 'pollingUnit') && parents.lga && (
							<CodeChip
								code={parents.lga}
								level='lga'
								compact
							/>
						)}
						{current.level === 'pollingUnit' && parents.ward && (
							<CodeChip
								code={parents.ward}
								level='ward'
								compact
							/>
						)}
					</div>
					{history.length > 1 && <BreadcrumbStrip history={history} />}
				</header>

				<div className='code-peek__body'>
					<CodePeekBody
						level={current.level}
						code={current.code}
					/>
				</div>

				<footer className='code-peek__footer'>
					<button
						type='button'
						className='code-peek__cta'
						onClick={promoteToPrimary}
					>
						Open full details
					</button>
				</footer>
			</aside>
		</>,
		document.body
	);
};
